import React, { memo, useMemo } from 'react';
import {
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { DiaryEntrySyncStatus } from 'graphql/types';
import { SmartDiaryEntry } from '../type';
import { LabelsTooltip } from './LabelsTooltip';
import { SmartListItemIcon } from './SmartListItemIcon';
import { SmartListItemText } from './SmartListItemText';
import { SmartListItemAction } from './SmartListItemAction';

type SmartListItemProps = { smartEntry: SmartDiaryEntry };
type ListStyleProps = {
  isSynced: boolean;
};

const useListStyle = makeStyles((theme) => ({
  root: {
    display: 'flex',
    width: `calc(100% - ${theme.spacing(3)})`,
    justifyContent: 'space-between'
  },
  gutters: {
    padding: theme.spacing(0.125, 1)
  },
  container: {
    background: ({ isSynced }: ListStyleProps) =>
      isSynced ? '#fafafa' : theme.palette.common.white,
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    paddingLeft: theme.spacing(1),
    '&:last-child': {
      borderBottom: '1px solid transparent'
    },
    '&:only-child': {
      marginBottom: theme.spacing(0),
      marginTop: theme.spacing(0),
      borderBottom: '1px solid transparent'
    }
  }
}));

const useStyles = makeStyles((theme) => ({
  listIcon: {
    minWidth: theme.spacing(3)
  },
  listAction: {
    display: 'flex',
    marginRight: theme.spacing(-2.75)
  },
  text: {
    display: 'flex',
    justifyContent: 'space-between'
  }
}));

export const SmartListItem: React.FC<SmartListItemProps> = memo(
  ({ smartEntry }: SmartListItemProps) => {
    const isSynced = useMemo(
      () => smartEntry.syncStatus === DiaryEntrySyncStatus.Synced,
      [smartEntry.syncStatus]
    );
    const classes = useStyles();
    return (
      <ListItem
        data-testid={`smart-entry-item_root_${smartEntry.id}`}
        classes={useListStyle({
          isSynced
        })}
      >
        <ListItemIcon className={classes.listIcon}>
          <SmartListItemIcon smartEntry={smartEntry} />
        </ListItemIcon>
        <LabelsTooltip smartEntry={smartEntry}>
          <ListItemText
            data-testid={`smart-entry-item_cpt_${smartEntry.id}`}
            primary={useMemo(
              () => (
                <SmartListItemText smartEntry={smartEntry} />
              ),
              [smartEntry]
            )}
          />
        </LabelsTooltip>
        <ListItemSecondaryAction className={classes.listAction}>
          <SmartListItemAction smartEntry={smartEntry} />
        </ListItemSecondaryAction>
      </ListItem>
    );
  }
);

SmartListItem.displayName = 'SmartListItem';
